import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardHeader, Col, Container, Row, UncontrolledTooltip } from 'reactstrap';
import withRouter from '../.././components/withRouter';
import TableContainer from '../.././components/TableContainerReactTable';
import { deletePlacement, getPlacements } from '../../api/placementApi';
import ImportPlacementModal from './ImportPlacementModal';
import { Placement } from '../../types/placement';
import { toast } from 'react-toastify';
import { useProfile } from '../../hooks/UserHooks';
import AddEditPlacementModal from './AddEditPlacementModal';
import numeral from 'numeral';

const Index = (props: any) => {
    const { userProfile } = useProfile();
    const [data, setData] = useState<Placement[]>([]);
    const [dataToEdit, setDataToEdit] = useState<Placement | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [isAddEditModalOpen, setIsAddEditModalOpen] = useState<boolean>(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState<boolean>(false);
    numeral.defaultFormat('0b');

    useEffect(() => {
        getPlacements().then((res) => {
            setData(res);
        }).finally(() => {
            setLoading(false);
        });
    }, []);

    const handleUpdateData = (newData: Placement) => {
        const updatedData = data.map((item) => {
            if (item.id === newData.id) {
                return newData;
            }
            return item;
        });
        setData(updatedData);
    };

    const handleAddData = (newData: Placement | Placement[]) => {
        if (Array.isArray(newData)) {
            setData([...data, ...newData]);
        } else {
            setData([...data, newData]);
        }
    };

    const handleRemoveData = (id: number) => {
        const updatedData = [...data].filter((item: any) => item.id !== id);
        setData(updatedData);
    };

    const toggleAddEditModal = () => {
        setIsAddEditModalOpen(!isAddEditModalOpen);
    };

    const toggleImportModal = () => {
        setIsImportModalOpen(!isImportModalOpen);
    };

    const handleDelete = (id: number) => {
        if (window.confirm('Tem certeza que deseja excluir este placement?')) {
            if (window.confirm('Esta ação é irreversível. Deseja continuar?')) {
                setLoading(true);
                deletePlacement(id)
                    .then(() => {
                        handleRemoveData(id);
                        toast.success('Placement excluído com sucesso');
                    })
                    .catch(() => {
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        }
    };

    const columns = [
        {
            header: 'Midia Cad',
            cell: (cell: any) => <span className="fw-semibold">{cell.getValue()}</span>,
            accessorKey: 'publisher.mediaCad',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Nome do placement',
            accessorKey: 'name',
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Tipo',
            accessorKey: 'type',
            cell: (cell: any) => {
                const tipo = cell.getValue();
                let badgeClass = 'badge-primary text-primary';
                if (tipo === 'display') badgeClass = 'badge-success text-success';
                else if (tipo === 'video') badgeClass = 'badge-warning text-warning';
                else if (tipo === 'audio') badgeClass = 'badge-info text-info';
                return <span className={`badge ${badgeClass}`}>{tipo}</span>;
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Sizes',
            accessorKey: 'sizes',
            cell: (cell: any) => {
                const row = cell.row.original;
                const actionCell: any[] = [];
                row.sizes?.forEach((size: string) => {
                    actionCell.push(<span
                        className="badge rounded-pill border border-primary text-primary">{size}</span>);
                });
                return <div className="d-flex gap-2">{actionCell}</div>;
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Sizes Expandidos',
            accessorKey: 'sizesExpandidos',
            cell: (cell: any) => {
                const row = cell.row.original;
                if (row.sizesExpandidos && row.sizesExpandidos.length > 0) {
                    const actionCell: any[] = [];
                    row.sizesExpandidos?.forEach((sizeExpandido: string) => {
                        actionCell.push(<span
                            className="badge rounded-pill border border-secondary text-secondary">{sizeExpandido}</span>);
                    });
                    return <div className="d-flex gap-2">{actionCell}</div>;
                }
                return '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Peso Máximo para Rich Media',
            accessorKey: 'maxRichMediaSizeInKb',
            cell: (cell: any) => {
                const row = cell.row.original;
                return numeral(row.maxRichMediaSizeInKb * 1000).format() || '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Peso Máximo para Imagem',
            accessorKey: 'maxImageSizeInKb',
            cell: (cell: any) => {
                const row = cell.row.original;
                return numeral(row.maxImageSizeInKb * 1000).format() || '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Aceita Audio/Video',
            accessorKey: 'acceptsAudioVideo',
            cell: (cell: any) => {
                const row = cell.row.original;
                const className = row.acceptsAudioVideo ? 'badge-success text-success' : 'badge-danger text-danger';
                return <span className={`badge ${className}`}>{row.acceptsAudioVideo ? 'sim' : 'não'}</span>;
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Peso Máximo para Vídeo',
            accessorKey: 'maxMediaSizeInKb',
            cell: (cell: any) => {
                const row = cell.row.original;
                return numeral(row.maxMediaSizeInKb * 1000).format() || '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Tempo máximo de vídeo/audio (s)',
            accessorKey: 'maxMediaDurationInSeconds',
            cell: (cell: any) => {
                const row = cell.row.original;
                return row.maxMediaDurationInSeconds || '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Recarregamento (s)',
            assessorKey: 'refreshInSeconds',
            cell: (cell: any) => {
                const row = cell.row.original;
                return row.refreshInSeconds || '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Status',
            accessorFn: (row: any) => row.status === 1 ? 'ativo' : 'inativo',
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Actions',
            cell: (cell: any) => {
                const row = cell.row.original;
                const actionCell = [];
                if (row.mediaKitUrl || row.mediaKitFile) {
                    actionCell.push(<>
                        <a href={row.mediaKitUrl || row.mediaKitFile}
                           id={'action_midia_kit_' + row.id}
                           target="_blank"
                           rel="noreferrer"
                           className="btn-icon material-shadow-none btn btn-primary"
                        >
                            <i className="ri-image-line"></i>
                        </a>
                        <UncontrolledTooltip
                            placement="top"
                            target={'action_midia_kit_' + row.id}
                        >
                            Mídia Kit
                        </UncontrolledTooltip>
                    </>);
                }

                if (userProfile?.role === 'ROLE_SUPER_ADMIN') {
                    actionCell.push(<>
                        <button className="btn-icon material-shadow-none btn btn-success"
                                id={'action_edit_' + row.id}
                                onClick={() => {
                                    setDataToEdit(row);
                                    toggleAddEditModal();
                                }}
                        ><i className="ri-pencil-fill fs-16"></i></button>
                        <UncontrolledTooltip
                            placement="top"
                            target={'action_edit_' + row.id}
                        >
                            Editar
                        </UncontrolledTooltip>
                    </>);
                }

                return <div className="d-flex gap-2">{actionCell}</div>;
            },
            enableSorting: false,
            enableColumnFilter: false,
        },
    ];

    if (userProfile?.role !== 'ROLE_VEICULO_ADMIN') {
        columns.splice(1, 0, {
            header: 'Publisher',
            accessorKey: 'publisher.name',
            enableColumnFilter: false,
            enableSorting: true,
        });
    }

    document.title = 'Blocos de Anúncios | Ad Pulse';
    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader>
                                    <Row className="g-4 align-items-center">
                                        <div className="col-sm">
                                            <h5 className="card-title mb-0">Blocos de Anúncios</h5>
                                        </div>
                                        <div className="col-sm-auto">
                                            {userProfile?.role === 'ROLE_SUPER_ADMIN' && (
                                                <button
                                                    type="button"
                                                    className="btn btn-success add-btn me-1"
                                                    id="create-btn"
                                                    onClick={() => {
                                                        setDataToEdit(null);
                                                        toggleAddEditModal();
                                                    }}
                                                >
                                                    <i className="ri-add-line align-bottom me-1"></i> Adicionar placements
                                                </button>
                                            )}

                                            {userProfile?.role === 'ROLE_SUPER_ADMIN' && (
                                                <button
                                                    type="button"
                                                    className="btn btn-success add-btn me-1"
                                                    id="create-btn"
                                                    onClick={toggleImportModal}
                                                >
                                                    <i className="ri-add-line align-bottom me-1"></i> Importar placements
                                                </button>
                                            )}
                                        </div>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <React.Fragment>
                                        <div className="table-responsive">
                                            {!loading ? <TableContainer
                                                    columns={(columns || [])}
                                                    data={(data || [])}
                                                    isGlobalFilter={true}
                                                    customPageSize={30}
                                                    SearchPlaceholder="Procurar..."
                                                /> :
                                                <div className="text-center">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Carregando...</span>
                                                    </div>
                                                </div>}
                                        </div>
                                    </React.Fragment>

                                    {userProfile?.role === 'ROLE_SUPER_ADMIN' && (
                                        <AddEditPlacementModal
                                            placement={dataToEdit}
                                            isOpen={isAddEditModalOpen}
                                            toggle={toggleAddEditModal}
                                            onAddData={handleAddData}
                                            onUpdateData={handleUpdateData}
                                        />
                                    )}
                                    {userProfile?.role === 'ROLE_SUPER_ADMIN' && (
                                        <ImportPlacementModal
                                            isOpen={isImportModalOpen}
                                            toggle={toggleImportModal}
                                            onAddData={handleAddData}
                                        />
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default withRouter(Index);